(ns story-builder.util.core
  (:require
   ["@sentry/browser" :as sentry]
   [alandipert.storage-atom :as sa]
   [cljs.math]
   [cljs.pprint :refer [cl-format]]
   [cljs.reader]
   [clojure.set :as set]
   [clojure.string :as str]
   [clojure.walk :as walk]
   [cognitect.transit :as transit]
   [goog.crypt.base64 :as base64]
   [goog.date.DateTime]
   [goog.dom :as gdom]
   [goog.i18n.NumberFormat]
   [goog.object :as gobj]
   [goog.string :as gstring]
   [goog.style :as gstyle]
   [re-frame.db :as rf-db]
   [story-builder.define :as define]
   [story-builder.util.url :as util.url])
  (:import
   [goog.async Debouncer Throttle]
   [goog.date UtcDateTime]))

(when (define/mobile?)
  (try
    (extend-type js/TouchList
      ISeqable
      (-seq [array] (array-seq array 0)))
    ;; Catch errors on MacOSX responsive mode, as they don't implement TouchList
    (catch :default _)))

(when define/frontend?
  (extend-type js/NodeList
    ISeqable
    (-seq [array] (array-seq array 0)))

  (extend-type js/FileList
    ISeqable
    (-seq [array] (array-seq array 0)))

  (extend-type js/HTMLCollection
    ISeqable
    (-seq [array] (array-seq array 0)))

  (extend-type js/HTMLCollection
    ICounted
    (-count [array] (.-length array)))

  (extend-type js/DOMStringList
    ISeqable
    (-seq [array] (array-seq array 0)))

  (extend-type js/DOMRect
    IEncodeClojure
    (-js->clj [this _]
      {:top    (.-top this)
       :height (.-height this)
       :y      (.-y this)
       :left   (.-left this)
       :right  (.-right this)
       :bottom (.-bottom this)}))

  (when define/studio?
    (extend-type js/CSSRuleList
      ISeqable
      (-seq [array] (array-seq array 0))))

  (when define/studio?
    (extend-type js/DataTransferItemList
      ISeqable
      (-seq [array] (array-seq array 0))))

  (when define/studio?
    (extend-type js/DataTransferItemList
      ICounted
      (-count [array] (.-length array))))

  (when-not (gdom/getElement "svg-sprite")
    (-> (js/fetch (str (if define/env-dev?
                         "http://localhost:3447/img/"
                         "//cdn.thenewsroom.io/images/story-builder/")
                       (if define/sdk?
                         "NWSStory-svg-sprite.svg"
                         (let [version (:version (js->clj (gobj/get js/window "_builder_config_" #js {})
                                                          :keywordize-keys true))]
                           (if (and (string? version)
                                    (str/includes? version "beta"))
                             "beta-svg-sprite.svg"
                             (case js/window.location.host
                               "beta-studio.nws.ai" "beta-svg-sprite.svg"
                               "test-studio.nws.ai" "test-svg-sprite.svg"
                               "svg-sprite.svg"))))))
        (.then (fn [response] (.text response)))
        (.then (fn [xml] (.parseFromString (js/window.DOMParser.) xml "text/xml")))
        (.then (fn [document]
                 (let [svg (.-documentElement document)]
                   (.setAttribute svg "id" "svg-sprite")
                   (.setAttribute svg "style" "display:none")
                   (gdom/insertChildAt js/window.document.body svg 0)))))))

(defn framed? [config]
  (let [flags (:flags config)]
    (or (:framed config)
        (:framed? flags)
        (and define/frontend?
             (try
               (not= js/window.self js/window.top)
               (catch :default _ true))))))

(defn report? [_config story-state]
  (and define/sdk?
       (not define/env-dev?)
       (:report? @story-state)))

;;; Formatting

(defn zero-pad
  "Pad number n with leading zeros to be length l."
  [n l]
  (gstring/format (str "%0" l "d") n))

(defn number
  "Format as a number to an optional number of decimal places."
  ([n]
   (number n 2))
  ([n dp]
   (if (js/Number.isNaN n)
     "-"
     (-> (goog.i18n.NumberFormat. (.-DECIMAL goog.i18n.NumberFormat.Format))
         (.setMaximumFractionDigits dp)
         (.format n)))))

(defn currency
  "Format as a currency."
  ([n]
   (.format (goog.i18n.NumberFormat. (.-CURRENCY goog.i18n.NumberFormat.Format))
            n))
  ([n currency]
   (.format (goog.i18n.NumberFormat. (.-CURRENCY goog.i18n.NumberFormat.Format)
                                     currency)
            n)))

(defn percent
  "Format as a percentage to an optional number of decimal places."
  ([n]
   (percent n 2))
  ([n dp]
   (str (number n dp) "%")))

(defn human-number [n]
  (condp < n
    1e6
    (str (number (/ n 1e6)) "M")
    5e3
    (str (number (/ n 1e3)) "K")
    n))

(defn trunc
  ([s] (trunc s 50))
  ([s n]
   (if s (str (subs s 0 (min (count s) n)) "...") "")))

(defn native-app? []
  (if define/frontend?
    (= (:inApp (util.url/qs->clj)) "true")
    true))

(defn disposable->function [disposable listener interval]
  (let [disposable-instance (disposable. listener interval)]
    (fn [& args]
      (.apply (.-fire disposable-instance) disposable-instance (to-array args)))))

(defn throttle
  "This throttle implementation fires the first and last event in the interval (it fires twice)."
  [listener interval]
  (disposable->function Throttle listener interval))

(defn debounce [listener interval]
  (disposable->function Debouncer listener interval))

(defn throttle-all
  "This throttle implementation only allows first event, and throttles all others."
  [f interval-ms]
  (let [last-called (atom 0)]
    (fn [& args]
      (let [current-time (.getTime (js/Date.))]
        (when (>= (- current-time @last-called) interval-ms)
          (reset! last-called current-time)
          (apply f args))))))

(defn file? [f]
  (= (type f) js/File))

(defn nan-to-0 [x]
  (if (js/Number.isNaN x) 0 x))

(defn inf-to-1 [x]
  (if (js/isFinite x) x 1))

(defn nil-to-0 [x]
  (if (nil? x) 0 x))

(defn slugify [text]
  (-> text
      (str/replace #"[^\w\s-]" "")
      str/trim
      str/lower-case
      (str/replace #"[-_\s]+" "-")))

(defn parse-rotate [str]
  (->>
   str
   (re-seq #"rotate\(((?:\-|\+)?[0-9]*\.?[0-9]+)(?=(?:deg|grad|turn|rad)\))")
   last last
   js/parseFloat
   nan-to-0))

(defn parse-scale [str]
  (if (and (string? str) (not (str/blank? str)))
    (-> #"scale\(([0-9]*\.?[0-9]+)(?=\))"
        (re-seq str)
        first
        second
        js/parseFloat
        nan-to-0)
    1))

(defn parse-css-value [default value]
  (let [res (-> value
                (str/replace #"px|em|%" "")
                js/parseFloat)]
    (if (js/Number.isNaN res) (or default 0) res)))

(defn parse-css-values-helper
  ([value]
   [value value value value])
  ([value1 value2]
   [value1 value2 value2 value1])
  ([one two three]
   [one two two three])
  ([one two three four]
   [one two three four]))

(defn parse-css-values [str default]
  (apply parse-css-values-helper (map (partial parse-css-value default) (str/split str " "))))

(defn stringify-style [m]
  (reduce (fn [res [k v]]
            (str res (name k) ":" v "; ")) "" m))

(defn parse-style [s]
  (->> (str/split (str/trim s) ";")
       (map str/trim)
       (map (fn [prop-val]
              (let [[_ prop value] (re-find #"(.*):(.*);?" prop-val)]
                [(keyword prop) value])))
       (into {})))

(comment
  (stringify-style {:font-style "italic", :font-weight "bold"})
  ;; => "font-style:italic; font-weight:bold; "
  (parse-style "font-style:italic; font-weight:bold; ")
  ;; => {:font-style "italic", :font-weight "bold"}
  )

(defn apply-if [coll test fn & args]
  (if (if (fn? test) (test coll) test) (apply fn coll args) coll))

(defn apply-to-vals
  "Same as medley/map-keys, only with the opposite arguments philosophy."
  [coll f & kv]
  (reduce-kv (fn [m k v] (assoc m k (apply f v kv))) (empty coll) coll))

(defn append-to-vals [col s]
  (apply-to-vals col str s))

(defn apply-to-keys
  "Similar to medley/map-vals, only with the opposite arguments philosophy."
  [coll f & kv]
  (reduce-kv (fn [m k v] (assoc m (apply f k kv) v)) (empty coll) coll))

(defn icon
  [type & additional-classes]
  [:svg.icon {:class (into [type] additional-classes)}
   [:use {:xlinkHref (str "#" type)}]])

(defn find-first
  "Faster alternative to (first (filter f coll))"
  [f coll]
  (reduce #(when (f %2) (reduced %2)) nil coll))

(defn filter-by
  "Filters map values by f"
  [f m]
  (into {} (filter (comp f val)) m))

(defn remove-by
  "Removes map values by function f"
  [f m]
  (into {} (remove (comp f val)) m))

(defn filter-nil
  "Filters out nil map values"
  [map]
  (filter-by some? map))

(defn filter-empty
  "Filters out the empty map values, i.e. nil, {}, (), [] etc."
  [map]
  (filter-by #(if (implements? ISeqable %)
                (seq %)
                (some? %)) map))

(defn update-last [vector f & kv]
  (apply update-in vector [(-> vector count dec)] f kv))

(defn reverse-kv [coll]
  (zipmap (vals coll) (keys coll)))

(defn reverse-merge
  "The same as merge, only parameters are applied in reverse order.
  Useful when already set keys are not to be overwritten."
  [& kvs]
  (apply merge (reverse kvs)))

(defn deep-merge
  "Recursively merges maps, and when encountering sets, unions them.
  Non-map/record values prefer the last non-nil value."
  [& maps]
  (letfn [(m [& xs]
            (cond
              (some #(and (map? %) (not (record? %))) xs)
              (apply merge-with m xs)

              (every? set? (filter some? xs))
              (apply clojure.set/union xs)

              :else (last (filter some? xs))))]
    (reduce m maps)))

(defn reverse-deep-merge [& kvs]
  (apply deep-merge (reverse kvs)))

(defn remove-blank-in [m ks]
  (update-in m ks #(filter-by (complement str/blank?) %)))

(defn builder
  "Utility function for creating nested lists at call time.

  Used with itself, it allows for:

  dev:cljs.user=> (def base (story-builder.util.core/builder identity [:div {:class \"SomeCSSClass\"}]))
  #'cljs.user/base
  dev:cljs.user=> (def child (story-builder.util.core/builder base [:b]))
  #'cljs.user/child
  dev:cljs.user=> (def inner-child (child \"Inner text\"))
  #'cljs.user/inner-child
  dev:cljs.user=> inner-child
  [:div {:class \"SomeCSSClass\"} [:b \"Inner text\"]] "
  [parent blocks]
  (fn [& args]
    (parent (cond
              (map? (first args))
              (reduce conj (update-last blocks merge (first args)) (rest args))
              :else
              (reduce conj blocks args)))))

(defn disable-element [tooltip]
  (builder identity [:span {:class "u-d-f u-fxd-c" :data-pseudo-tooltip tooltip}]))

(defn index-of [coll v]
  (let [i (count (take-while #(not= v %) coll))]
    (when (or (< i (count coll))
              (= v (last coll)))
      i)))

(defn idx-by
  ([coll f]
   (loop [idx 0
          s (seq coll)]
     (if (nil? s)
       nil
       (let [[elm & next-elms] s]
         (if (f elm)
           idx
           (recur (inc idx) next-elms))))))
  ([coll k v]
   (idx-by coll #(= (get % k) v))))

(defn vec-remove
  [coll index]
  (into (subvec coll 0 index) (subvec coll (inc index))))

(defn vec-remove-val
  [coll val]
  (if-let [index (index-of coll val)]
    (vec-remove coll index)
    coll))

(defn vec-insert
  [coll index el]
  (-> (subvec coll 0 index)
      (conj el)
      (into (subvec coll index))))

(defn vec-move
  [coll pos1 pos2]
  (let [el (nth coll pos1)]
    (if (= pos1 pos2)
      coll
      (vec-insert (vec-remove coll pos1) pos2 el))))

(defn vec-merge [xs]
  (let [max-length (apply max (map count xs))
        padded     (map (fn [x] (concat x (repeat (- max-length (count x)) nil))) xs)]
    (apply mapv (fn [& x] (apply merge x)) padded)))

(defn closest
  ([coll x]
   (closest coll x 1))
  ([coll x n]
   (cond-> (take n (sort-by #(js/Math.abs (- % x)) coll))
     (= n 1)
     (first))))

(defn conj-unique [v item]
  (if (some #(= % item) v)
    v
    (conj v item)))

(defn set-xor
  "Performs a xor on sets, aka symmetric difference.
  A XOR B = (A OR B) - (A AND B)"
  [& ks]
  (set/difference (apply set/union ks) (apply set/intersection ks)))

(defn non-recursive-js->clj
  [obj]
  (if (goog/isObject obj)
    (persistent!
     (reduce
      (fn [result key]
        (let [v (goog.object/get obj key)]
          (assoc! result (keyword key) v)))
      (transient {})
      (goog.object/getKeys obj)))
    obj))

(defn int-word [n] (cl-format nil "~r" n))

(defn ordinal-numeral [n]
  (let [ending (case n
                 1 "st"
                 2 "nd"
                 3 "rd"
                 "th")]
    (str n ending)))

(defn human-file-size [size]
  (when (number? size)
    (let [i    (-> size js/Math.log (/ (js/Math.log 1024)) js/Math.floor)
          unit (nth ["B" "kB" "MB" "GB" "TB"] i)]
      (-> size (/ (js/Math.pow 1024 i)) (.toFixed 2) (str " " unit)))))

(defn file-info-from-input [input cb]
  (if-let [file (first (.-files input))]
    (let [reader (js/FileReader.)]
      (set! (.-onload reader)
            (fn [e] (cb {:name (last (str/split (.-value input) #"\\"))
                         :url  (.. e -target -result)})))
      (.readAsDataURL reader file))
    (cb {:url ""})))

(defn download-uri [uri name]
  (let [a (js/document.createElement "a")]
    (.setAttribute a "download" name)
    (.setAttribute a "href" uri)
    (js/document.body.appendChild a)
    (.click a)
    (js/document.body.removeChild a)))

(defrecord TempID [uuid data])

(deftype TempIDHandler []
  Object
  (tag [_ _] "tempid")
  (rep [_ ^js v] #js [(.-uuid v) (.-data v)])
  (stringRep [_ ^js v] (str (.-uuid v) (.-data v))))

(def transit-write-handlers
  {TempID      (TempIDHandler.)
   UtcDateTime (transit/write-handler
                (constantly "m")
                (fn [v] (.getTime v))
                (fn [v] (str (.getTime v))))})

(def transit-read-handlers
  {"tempid" (fn [[uuid data]] (TempID. uuid data))
   "m"      (transit/read-handler (fn [s] (UtcDateTime/fromTimestamp s)))})

(swap! sa/transit-write-handlers merge transit-write-handlers)
(swap! sa/transit-read-handlers merge transit-read-handlers)

(defn write-transit [d]
  (let [w (transit/writer :json {:handlers transit-write-handlers})]
    (transit/write w d)))

(defn read-transit [s]
  (let [r (transit/reader :json {:handlers transit-read-handlers})]
    (transit/read r s)))

(defn read-config [config]
  (try
    (read-transit config)
    (catch :default _
      (cljs.reader/read-string {:readers {'story-builder.util.core.TempID (fn [{:keys [uuid data]}] (->TempID uuid data))}} config))))

(defn sanitize-html [content]
  (when content
    (-> content
        (str/replace #"&quot;" "\"")
        (str/replace #"<br>" "\n")
        (str/replace #"&nbsp;" " ")
        (str/replace #"&amp;" "&")
        (str/replace #"(:?<b>|</b>|<i>|</i>|<s>|</s>|<u>|</u>)" "")
        (str/replace #"&\w+;" ""))))

(defn strip-html [html]
  (let [doc (.parseFromString (js/DOMParser.) html "text/html")]
    (or (.. doc -body -textContent) "")))

(defn catch-play-promise-abort-error
  ([promise]
   (catch-play-promise-abort-error promise nil))
  ([promise on-autoplay-failed]
   (some-> promise
           (.catch (fn [e]
                     (let [autoplay-failed? (and (instance? js/DOMException e)
                                                 (contains? #{"AbortError"
                                                              "NotAllowedError"} (.-name e)))]
                       (if autoplay-failed?
                         (do
                           (js/console.warn "Attempt to autoplay with sound failed:" e)
                           (when (fn? on-autoplay-failed)
                             (on-autoplay-failed)))
                         (throw e))))))))

(defn exception [data]
  (let [error (if (instance? js/Error data)
                data
                (js/Error. data))]
    (if define/sentry?
      (try
        (throw error)
        (catch js/Error e (sentry/captureException e)))
      (js/console.error data))))

(defn fire-tracker
  [url]
  (if (str/includes? url "echo.nws.press")
    (let [script (gdom/createDom "script" #js {:src url :type "text/javascript"})]
      (gdom/appendChild js/document.body script))
    (gdom/createDom "img" #js {:src url :width "1px" :height "1px"})))

(defn send-beacon [url]
  (let [firefox?   (and (exists? js/navigator.userAgent)
                        (str/includes? js/navigator.userAgent "Firefox"))
        fetch-opts #js {:keepalive true}]
    (if firefox?
      ;; Firefox requires keepalive for requests to complete during beforeunload
      (js/fetch url fetch-opts)
      (try
        (when-not (js/navigator.sendBeacon url)
          ;; sendBeacon returns false if blocked/failed
          (js/fetch url fetch-opts))
        (catch :default _
          (js/console.warn "sendBeacon failed, falling back to fetch")
          (js/fetch url fetch-opts))))))

(defn post-message
  ([msg] (post-message msg {:origin "*"}))
  ([msg opts]
   (let [{:keys [origin
                 story-state
                 target]
          :or   {origin "*"}} opts
         msg                  (clj->js msg)]
     (when-not (:safeframe? @story-state)
       (if target
         (.postMessage target msg origin)
         (do
           (.postMessage js/window msg origin)
           (.postMessage js/window.parent msg origin)
           (.postMessage js/window.top msg origin)))))))

(defn ga-enable [code]
  (aset js/window (str "ga-disable-" code) false))

(defn ga-disable [code]
  (aset js/window (str "ga-disable-" code) true))

(defn ga-send-event
  [{:keys [action
           category
           label]}]
  (when-not define/env-dev?
    (when-let [ga (gobj/get js/window "ga")]
      (when-let [tracker (first (js-invoke ga "getAll"))]
        (js-invoke tracker "send" "event" category action label)))))

(defn copy-to-clipboard [x]
  (-> (js/navigator.clipboard.writeText x)
      (.catch (fn [e] (js/console.error "Error copying to clipboard. " e)))))

(defn copy-item-to-clipboard [clipboard-item]
  (-> (js/navigator.clipboard.write [clipboard-item])
      (.catch (fn [e] (js/console.error "Error copying to clipboard. " e)))))

(defn encode-gql-id [type id]
  (base64/encodeString (str/join ":" [type id])))

(defn decode-id
  [object-id]
  (when object-id
    (-> object-id (base64/decodeString) (str/split ":") second int)))

(defn checkbox-values [classname]
  (filter identity
          (map
           (fn [el]
             (when (true? (.-checked el))
               (.-value el)))
           (gdom/getElementsByClass classname))))

(defn round-to-two-decimals [f]
  (/ (.round js/Math (* 100 f)) 100))

(defn shopify-format-price
  [format price]
  (cond
    (str/includes? format "{{amount}}")
    (str/replace format "{{amount}}" price)
    (str/includes? format "{{amount_no_decimals}}")
    (str/replace format "{{amount_no_decimals}}" price)
    (str/includes? format "{{amount_with_comma_separator}}")
    (str/replace format "{{amount_with_comma_separator}}" price)
    (str/includes? format "{{amount_no_decimals_with_comma_separator}}")
    (str/replace format "{{amount_no_decimals_with_comma_separator}}" price)
    (str/includes? format "{{amount_with_apostrophe_separator}}")
    (str/replace format "{{amount_with_apostrophe_separator}}" price)
    :else price))

(defn scene-comparator [s1 s2]
  (let [s1-layers      (:layers s1)
        s2-layers      (:layers s2)
        s1-type        (:type s1)
        s2-type        (:type s2)
        s1-layout      (:layout s1)
        s2-layout      (:layout s2)
        s1-layer-types (set (map :type s1-layers))
        s2-layer-types (set (map :type s2-layers))
        s1-title       (find-first #(= (:type %) "title") s1-layers)
        s2-title       (find-first #(= (:type %) "title") s2-layers)
        s1-text        (find-first #(= (:type %) "text") s1-layers)
        s2-text        (find-first #(= (:type %) "text") s2-layers)
        s1-image?      (find-first #(= (:type %) "img") s1-layers)
        s2-image?      (find-first #(= (:type %) "img") s2-layers)
        s1-video?      (find-first #(= (:type %) "video") s1-layers)
        s2-video?      (find-first #(= (:type %) "video") s2-layers)
        layout-diff    (js/Math.abs (- s1-layout s2-layout))
        title-diff     (js/Math.abs (- (count (:content s1-title)) (count (:content s2-title))))
        text-diff      (js/Math.abs (- (count (:content s1-text)) (count (:content s2-text))))]
    (+ layout-diff
       title-diff
       text-diff
       (count (set/difference s1-layer-types s2-layer-types))
       (if (= s1-type s2-type) 0 10)
       (if (and s1-image? s2-image?) 0 10)
       (if (and s1-video? s2-video?) 0 10))))

;; Math

(defn vector-*-scalar [vector scalar]
  (map * vector (repeat 2 scalar)))

(comment
  (vector-*-scalar [1 2] 2));; => (2 4)

(defn vector-div-scalar [vector scalar]
  (map / vector (repeat 2 scalar)))

(comment
  (vector-div-scalar [2 4] 2));; => (1 2)

(defn vector-*-vector [a b]
  (apply + (map * a b)))

(comment
  (vector-*-vector [1 2] [1 2]));; => 5

(defn matrix-*-vector [matrix vector]
  (map (partial vector-*-vector vector) matrix))

(comment
  (matrix-*-vector [[1 2] [1 2]] [1 2]));; => (5 5)

;; As per this article:
;; https://en.wikipedia.org/wiki/Rotation_matrix#Ambiguities
;; Layer transform means transforming the Coordinate System, which makes all rotations *alias* ones.

(defn rotate-rad
  ([vector angle]
   (let [sin             (js/Math.sin angle)
         cos             (js/Math.cos angle)
         rotation-matrix [[cos (- sin)]
                          [sin    cos]]]
     (matrix-*-vector rotation-matrix vector)))
  ([vec angle pivot]
   (mapv + (rotate-rad (mapv - vec pivot) angle) pivot)))

(defn rotate-deg
  ([v angle]
   (rotate-rad v (cljs.math/to-radians angle)))
  ([v angle pivot]
   (rotate-rad v (cljs.math/to-radians angle) pivot)))

(defn percent-position-between-two-points [x first-point points-distance]
  (* 100 (/ (- x first-point) points-distance)))

(comment
  (percent-position-between-two-points 1 1 4);; => 0
  (percent-position-between-two-points 2 1 4);; => 25
  (percent-position-between-two-points 3 1 4);; => 50
  (percent-position-between-two-points 4 1 4));; => 100

(defn percent-magnitude [a b]
  (/ (* b 100) a))

(comment
  (percent-magnitude 40 20));; => 50

(defn percent-of [a b]
  (/ (* a b) 100))

(comment
  (percent-of 100 50);; => 50
  (percent-of 100 20);; => 20
  (percent-of 100 80);; => 80
  (percent-of 40 50));; => 20

(defn round
  ([nr reference]
   (* (js/Math.round (/ nr reference)) reference))
  ([nr]
   (round nr 1)))

(defn round-up
  ([nr reference]
   (* (js/Math.ceil (/ nr reference)) reference))
  ([nr]
   (round-up nr 1)))

(defn round-down
  ([nr reference]
   (* (js/Math.floor (/ nr reference)) reference))
  ([nr]
   (round-down nr 1)))

(defn inside-rectangle? [[rect-x rect-y width height] [point-x point-y]]
  (and (< rect-x point-x)
       (> (+ rect-x width) point-x)
       (< rect-y point-y)
       (> (+ rect-y height) point-y)))

(defn inside-circle? [[[c-x c-y] r] [p-x p-y]]
  (let [dx (- p-x c-x)
        dy (- p-y c-y)]
    (< (+ (* dx dx) (* dy dy)) (* r r))))

(defn mid-point [a b]
  (mapv #(/ % 2) (map + a b)))

(defn dot [a b]
  (reduce + (map * a b)))

(defn cross [a b]
  (reduce - (map * a (reverse b))))

(defn sign
  ([a]
   (sign 1 a))
  ([default a]
   (if (zero? a)
     default
     (if (pos? a) 1 -1))))

(defn ensure-sign [a nr]
  (let [a (if (zero? a) 1 a)]
    (if (= (sign a) (sign nr)) a (- a))))

(defn rad-angle-between-two-vectors [a b]
  (js/Math.atan2 (cross a b) (dot a b)))

(defn closest-angle
  "Every vector can be oriented by two angles.
  Returns the least one."
  [angle]
  (cond
    (> angle 180) (- angle 360)
    (< angle -180) (+ angle 360)
    :else angle))

(defn opposite-angle
  "The same angle, measured in the opposite clockwise direction."
  [angle]
  (* -1 (sign angle) (- 360 (js/Math.abs angle))))

(defn angle [vector]
  (-> (apply js/Math.atan2 vector) (- (/ js/Math.PI 2)) cljs.math/to-degrees round -))

(defn magnitude [[a b]]
  (js/Math.sqrt (+ (* a a) (* b b))))

(defn make-vector
  ([angle]
   (make-vector angle 1))
  ([angle magnitude]
   (->>
    angle
    (rotate-deg [1 0])
    (map (partial * magnitude)))))

(defn unit-vector [vector]
  (map / vector (repeat 2 (magnitude vector))))

(defn vector-projection
  "Calculates the scalar component of a in the direction of b, i.e. the projection of a on b.
  http://people.math.harvard.edu/~jjchen/math21a/handouts/vector-ops.html#subsection-16"
  [a b]
  (dot a (unit-vector b)))

(defn ensure-ar
  [[width height] ar]
  (let [width  (js/Math.abs width)
        height (js/Math.abs height)]
    (if (>= (* height ar) width)
      [(* height ar) height]
      [width (/ width ar)])))

;; Conversions

(defn px-to-em [v]
  (when v
    (/ v (get-in @rf-db/app-db [:utils :reference-em]))))

(defn em-to-px [v]
  (when v
    (* v (get-in @rf-db/app-db [:utils :reference-em]))))

(defn encode-copy-data [data type]
  (->> data
       (write-transit)
       (str define/copy-header type)))

(defn decode-copy-data [data type]
  (let [copy-header (str define/copy-header type)]
    (-> data
        (str/replace-first copy-header "")
        (read-transit))))

(defn copy-data-type? [data type]
  (let [copy-header (str define/copy-header type)]
    (str/starts-with? data copy-header)))

(defn copy-data [event data type]
  (.preventDefault event)
  (when (some? data)
    (let [data (encode-copy-data data type)]
      (-> event .-clipboardData (.setData "text/plain" data)))))

(defn paste-data [event type]
  (.preventDefault event)
  (let [data (-> event .-clipboardData (.getData "text/plain"))]
    (when (copy-data-type? data type)
      (decode-copy-data data type))))

(defn mean
  "Calculates the mean average for a sequence of numbers."
  [numbers]
  (if (empty? numbers)
    0
    (/ (reduce + numbers) (count numbers))))

(defn median
  "Calculates the median average for a sequence of numbers."
  [ns]
  (let [ns (sort ns)
        cnt (count ns)
        mid (bit-shift-right cnt 1)]
    (if (odd? cnt)
      (nth ns mid)
      (/ (+ (nth ns mid) (nth ns (dec mid))) 2))))

(defn use-ref
  "A little like a React hook, used to store and read a reference to a dom element created by React."
  []
  (let [atom-ref (atom nil)]
    [atom-ref (fn [el] (reset! atom-ref el))]))

(defn when-every-some?
  "Returns a function which work the same as f when applied to non-nil arguments
   but which returns nil if any of the argument is nil."
  [f]
  (fn [& args]
    (when (every? some? args)
      (apply f args))))

(comment
  ((when-every-some? -) 5 3)
  ((when-every-some? -) nil 3)
  ((when-every-some? -) 5 nil))

(defn seq-indexed
  "Returns an indexed sequence of the elements in the collection."
  [coll]
  (map-indexed (fn [index element] [index element])
               coll))

(comment
  (seq-indexed [:a :b :c]) ;; => ([0 :a] [1 :b] [2 :c])
  (seq-indexed "hello"))    ;; => ([0 "h"] [1 "e"] [2 "l"] [3 "l"] [4 "o"])

(defn find-all-nested
  [m k]
  (->> (tree-seq #(or (map? %) (vector? %)) identity m)
       (filter #(and (map? %) (k %)))
       (map k)
       set
       vec))

(when define/frontend?
  (defn jwt-decode [token]
    (when token
      (-> (str/split token ".")
          (second)
          (js/window.atob)
          (js/JSON.parse)
          (js->clj :keywordize-keys true)))))

(defn no-op
  "A function that does nothing and returns nil.

  This function should be used instead of #() because #() will create a new instance of the function each time it is evaluated.
  Useful for React's diffing algorithm."
  [& _])

(comment
  (let [f (fn [] #())]
    [(= #() #())
     (= (f) (f))
     (= no-op no-op)])) ;; => [false false true]

(defn capitalize-first [s]
  (apply str
         (map-indexed
          (fn [i c]
            (if (zero? i)
              (str/capitalize c)
              c))
          s)))

(defn get-window-selection-at-range-zero []
  (when-let [^js selection (.getSelection js/window)]
    (when (pos? (.-rangeCount selection))
      (.getRangeAt selection 0))))

(defn selection-collapsed? []
  (boolean (when-let [selection-range (get-window-selection-at-range-zero)]
             (.-collapsed selection-range))))

(defn remove-<br> [s]
  (when (some? s)
    (-> s
        (str/replace #"<br>" " ")
        (str/trim))))

(defn not-blank-str [s]
  (when-not (str/blank? s)
    s))

(defn clamp-index
  "Clamps an index within the bounds of a collection."
  [index coll]
  (when (int? index)
    (-> index
        (min (dec (count coll)))
        (max 0))))

(defn invalid-index->nil
  "Returns nil if the index is not within the bounds of the collection, returns the index otherwise."
  [index coll]
  (when (and (int? index)
             (< -1 index (count coll)))
    index))

(defn event-over-element?
  [event element]
  (let [rect (.getBoundingClientRect element)
        x    (.-clientX event)
        y    (.-clientY event)]
    (and (>= x (.-left rect))
         (< x (.-right rect))
         (>= y (.-top rect))
         (< y (.-bottom rect)))))

(defn overlapping-elements? [el1 el2]
  (let [rect1 (.getBoundingClientRect el1)
        rect2 (.getBoundingClientRect el2)]
    (not (or (< (.-right rect1) (.-left rect2))
             (> (.-left rect1) (.-right rect2))
             (< (.-bottom rect1) (.-top rect2))
             (> (.-top rect1)  (.-bottom rect2))))))

(defn single-quoted [s]
  (when s
    (if (or (str/blank? s)
            (str/starts-with? s "'"))
      s
      (str "'" s "'"))))

(defn replace-value
  "Replaces a value (as opposed to a key) in an arbitrary EDN data structure."
  [data old-value new-value]
  (walk/prewalk (fn [node]
                  (if (= node old-value)
                    new-value
                    node))
                data))

(defn number-of-lines-of-text
  "Return the number of lines of wrapped text in the element."
  [dom-el]
  (let [height      (.-offsetHeight dom-el)
        line-height (js/parseInt (gstyle/getComputedStyle dom-el "line-height"))]
    (/ height line-height)))

(defn open-window [url]
  (let [w (js/window.open url "_blank")]
    (if w
      (set! (.-opener w) nil)
      (js/window.open url "_self"))))

(defn ctrl-or-cmd? [e]
  (or (.-ctrlKey e)
      (.-metaKey e)))

(defn uuid-string? [s]
  (and (string? s)
       (uuid? (parse-uuid s))))

(defn with-lock
  [lock-atom f]
  (let [acquired? (compare-and-set! lock-atom false true)]
    (when acquired?
      (try
        (f)
        (finally
          (reset! lock-atom false))))))

(defn log [& args]
  (let [style "background: linear-gradient(#e66465, #9198e5); color: white; padding: 2px 6px; border-radius: 4px; font-weight: bold;"
        label (if (string? (first args))
                [(str "%c[NWS] " (first args)) style]
                ["%c[NWS]" style])
        data (if (string? (first args)) (rest args) args)]
    (apply js/console.log (concat label data))))